// Shared "selected row" plumbing for interactive visuals (cards / tree / feeds /
// table). When a visual is interactive (an `onSelect` handler is supplied), a
// click should both drive the page var (via onSelect) AND visibly mark the
// clicked element so the user can see what is currently selected — the same
// affordance a transaction list row gives. This module keeps that behaviour
// uniform across every renderer.

import { useCallback, useState } from 'react'
import type { SelectInfo } from './drillThrough'

/** A stable identity string for a data row, independent of its array index so
 *  the highlight survives re-render and data reorder. Built from the row's own
 *  column values (jsonData rows are keyed by column alias). */
export function rowKey(row: Record<string, unknown> | null | undefined): string {
  if (!row || typeof row !== 'object') return ''
  try {
    return Object.keys(row)
      .sort()
      .map((k) => `${k}=${String((row as Record<string, unknown>)[k])}`)
      .join('')
  } catch {
    return ''
  }
}

/** Tracks which row is selected within a single visual and wraps the visual's
 *  onSelect so one click highlights locally and drives the bound page var.
 *  `isSelected` is always false until the user clicks, and stays false when the
 *  visual is display-only (no onSelect). */
export function useRowSelection(onSelect?: (row: Record<string, unknown>, info?: SelectInfo) => void) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const select = useCallback(
    (row: Record<string, unknown>, info?: SelectInfo) => {
      setSelectedKey(rowKey(row))
      onSelect?.(row, info)
    },
    [onSelect],
  )
  const isSelected = useCallback(
    (row: Record<string, unknown>) => selectedKey != null && rowKey(row) === selectedKey,
    [selectedKey],
  )
  // `select` is only meaningful when the visual is interactive.
  return { selectedKey, select: onSelect ? select : undefined, isSelected }
}

// Shared selected-state visual treatment (uses the page theme's primary colour
// with sensible fallbacks so it reads on both light and dark surfaces).
export const SELECT_RING = '0 0 0 2px var(--pg-primary, var(--color-accent, #2563eb))'
export const SELECT_RING_INSET = 'inset 3px 0 0 0 var(--pg-primary, var(--color-accent, #2563eb))'
export const SELECT_BG = 'var(--pg-primary-soft, var(--color-accent-weak, rgba(37,99,235,0.12)))'
